import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Car, ArrowLeft, MapPin, User, Phone, Mail, Plane, Calendar as CalendarIcon,
  Clock, Users, DollarSign, Loader2, Info, Navigation, RefreshCw, RotateCcw,
  Building, Baby, Plus, CheckCircle, Sparkles, Send, X
} from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SortableStopItem from "../components/SortableStopItem";
import RouteMapPreview from "../components/RouteMapPreview";
import { AIBookingAssistant } from "../components/booking/AIBookingAssistant";
import { BookingTypeSelector } from "../components/booking/BookingTypeSelector";
import { VehicleSelection } from "../components/booking/VehicleSelection";
import { ReturnTripSection } from "../components/booking/ReturnTripSection";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { ExtrasSelection } from "../components/booking/ExtrasSelection";

const BookingForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [pricing, setPricing] = useState(null);
  
  // AI Assistant state
  const [aiBookingText, setAiBookingText] = useState("");
  const [parsingAiBooking, setParsingAiBooking] = useState(false);
  
  // Initialize date/time from URL params if available
  const urlDatetime = searchParams.get("datetime");
  const urlDate = searchParams.get("date");
  const urlTime = searchParams.get("time");
  
  const [date, setDate] = useState(() => {
    if (urlDate) {
      try {
        return new Date(urlDate + "T00:00:00");
      } catch {
        return new Date();
      }
    }
    if (urlDatetime) {
      try {
        return new Date(urlDatetime);
      } catch {
        return new Date();
      }
    }
    return new Date();
  });
  const [time, setTime] = useState(() => {
    if (urlTime) {
      return urlTime;
    }
    if (urlDatetime) {
      try {
        const dt = new Date(urlDatetime);
        return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
      } catch {
        return "09:00";
      }
    }
    return "09:00";
  });
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [priceQuote, setPriceQuote] = useState(null);
  const [manualDistance, setManualDistance] = useState("");
  const [distanceError, setDistanceError] = useState("");
  const [pickupSelected, setPickupSelected] = useState(false);
  const [dropoffSelected, setDropoffSelected] = useState(false);
  
  // Return trip state
  const [includeReturn, setIncludeReturn] = useState(false);
  const [returnDate, setReturnDate] = useState(null);
  const [returnTime, setReturnTime] = useState("17:00");
  
  // Corporate and Extras state
  const [corporateClients, setCorporateClients] = useState([]);
  const [selectedCorporate, setSelectedCorporate] = useState("");
  const [corporateReference, setCorporateReference] = useState("");
  const [extras, setExtras] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  
  // Initialize form with URL params for AI-parsed bookings
  const [formData, setFormData] = useState({
    pickup_location: searchParams.get("pickup") || "",
    dropoff_location: searchParams.get("dropoff") || "",
    passenger_name: searchParams.get("name") || user?.name || "",
    passenger_email: user?.email || "",
    passenger_phone: searchParams.get("phone") || user?.phone || "",
    num_passengers: parseInt(searchParams.get("passengers")) || 1,
    vehicle_type: searchParams.get("vehicle") || "sedan",
    booking_type: "transfer", // "transfer" or "hourly"
    hours: 2,
    flight_number: searchParams.get("flight") || "",
    flight_arrival_time: "",
    special_requirements: "",
    notes: "",
    price: 0,
    extra_stops: "",
    reference_number: "", // Corporate reference/file number
    stops: [] // Array of intermediate stops
  });
  
  // Stops management
  const [stops, setStops] = useState([]);
  const [stopIds, setStopIds] = useState([]);
  const stopsKey = JSON.stringify(stops);

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleStopDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = stopIds.indexOf(active.id);
      const newIndex = stopIds.indexOf(over.id);
      setStops(arrayMove(stops, oldIndex, newIndex));
      setStopIds(arrayMove(stopIds, oldIndex, newIndex));
    }
  };

  const addStop = () => {
    setStops([...stops, ""]);
    setStopIds([...stopIds, `stop-${Date.now()}`]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
    setStopIds(stopIds.filter((_, i) => i !== index));
  };

  const updateStop = (index, value) => {
    const newStops = [...stops];
    newStops[index] = value;
    setStops(newStops);
  };

  // Create Invoice option for admin
  const [createInvoice, setCreateInvoice] = useState(false);
  const [invoiceDueDate, setInvoiceDueDate] = useState(
    format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), "yyyy-MM-dd")
  );
  
  // Payment method option for admin/staff
  const [paymentMethod, setPaymentMethod] = useState("credit_card");
  
  // Send as Quote option for admin/staff
  const [sendAsQuote, setSendAsQuote] = useState(false);

  // Sync URL params to formData after component mounts
  useEffect(() => {
    const pickup = searchParams.get("pickup");
    const dropoff = searchParams.get("dropoff");
    const urlDate = searchParams.get("date");
    const urlTimeParam = searchParams.get("time");
    
    console.log("URL Params:", { pickup, dropoff, urlDate, urlTimeParam });
    
    if (pickup || dropoff) {
      setFormData(prev => ({
        ...prev,
        pickup_location: pickup ? decodeURIComponent(pickup) : prev.pickup_location,
        dropoff_location: dropoff ? decodeURIComponent(dropoff) : prev.dropoff_location
      }));
    }
    
    if (urlDate) {
      try {
        const parsedDate = new Date(urlDate + "T12:00:00");
        console.log("Setting date to:", parsedDate);
        setDate(parsedDate);
      } catch (e) {
        console.error("Invalid date from URL:", e);
      }
    }
    
    if (urlTimeParam) {
      console.log("Setting time to:", urlTimeParam);
      setTime(urlTimeParam);
    }
  }, []); // Run only once on mount

  useEffect(() => {
    fetchPricing();
    fetchExtras();
    if (user?.role === "admin" || user?.role === "staff") {
      fetchCorporateClients();
    }
  }, [user]);

  const fetchPricing = async () => {
    try {
      const response = await api.get("/vehicles/pricing");
      setPricing(response.data);
    } catch (error) {
      console.error("Failed to load pricing");
    }
  };

  const fetchExtras = async () => {
    try {
      const response = await api.get("/extras");
      setExtras(response.data);
    } catch (error) {
      console.error("Failed to load extras");
    }
  };

  const fetchCorporateClients = async () => {
    try {
      const response = await api.get("/corporate-clients");
      setCorporateClients(response.data);
    } catch (error) {
      console.error("Failed to load corporate clients");
    }
  };

  const handleCorporateSelect = (clientId) => {
    setSelectedCorporate(clientId);
    if (clientId) {
      const client = corporateClients.find(c => c.id === clientId);
      if (client) {
        // Keep passenger fields empty for admin to fill, but store corporate ID
        setFormData(prev => ({
          ...prev,
          passenger_name: "",
          passenger_email: "",
          passenger_phone: ""
        }));
      }
    }
  };

  const handleExtraToggle = (extraId) => {
    setSelectedExtras(prev => 
      prev.includes(extraId) 
        ? prev.filter(id => id !== extraId)
        : [...prev, extraId]
    );
  };

  const getExtrasTotal = () => {
    return selectedExtras.reduce((total, extraId) => {
      const extra = extras.find(e => e.id === extraId);
      return total + (extra?.price || 0);
    }, 0);
  };

  const getTotalPrice = () => {
    return (parseFloat(formData.price) || 0) + getExtrasTotal();
  };

  const calculateDistance = useCallback(async () => {
    if (!formData.pickup_location || (!formData.dropoff_location && formData.booking_type === "transfer")) {
      return;
    }
    
    setCalculatingDistance(true);
    setDistanceError("");
    try {
      const filteredStops = stops.filter(s => s && s.trim() !== "");
      const payload = {
        origin: formData.pickup_location,
        destination: formData.dropoff_location || formData.pickup_location
      };
      if (filteredStops.length > 0) {
        payload.waypoints = filteredStops;
      }
      const response = await api.post("/distance/calculate", payload);
      setDistanceInfo(response.data);
      setManualDistance(response.data.distance_km.toString());
      // Auto-calculate price with new distance
      await calculatePrice(response.data.distance_km);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Distance calculation not available";
      setDistanceError(errorMsg);
      setDistanceInfo(null);
      toast.error("Auto-calculation unavailable. Please enter distance manually.");
    } finally {
      setCalculatingDistance(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.pickup_location, formData.dropoff_location, formData.booking_type, stops]);

  const handleManualDistanceChange = (value) => {
    setManualDistance(value);
    const km = parseFloat(value) || 0;
    if (km > 0) {
      calculatePrice(km);
    }
  };

  const calculatePrice = async (distanceKm = null) => {
    try {
      const numStops = stops.length; // Count all stops for instant price feedback
      const requestData = {
        origin: formData.pickup_location || "N/A",
        destination: formData.dropoff_location || "As directed",
        vehicle_type_slug: formData.vehicle_type,
        booking_type: formData.booking_type,
        distance_km: distanceKm || parseFloat(manualDistance) || distanceInfo?.distance_km || 0,
        hours: formData.booking_type === "hourly" ? formData.hours : null,
        num_stops: numStops
      };
      
      const response = await api.post("/pricing/quote", requestData);
      setPriceQuote(response.data);
      setFormData(prev => ({ ...prev, price: response.data.price }));
    } catch (error) {
      console.error("Failed to calculate price");
    }
  };

  // Recalculate price when vehicle, booking type, hours, or stops change
  useEffect(() => {
    if (pricing && formData.vehicle_type) {
      calculatePrice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.vehicle_type, formData.booking_type, formData.hours, stopsKey]);

  // Auto-recalculate distance when stops content changes (for transfers)
  useEffect(() => {
    if (formData.booking_type === "transfer" && formData.pickup_location && formData.dropoff_location) {
      const hasContent = stops.some(s => s && s.trim() !== "");
      if (hasContent || stops.length === 0) {
        calculateDistance();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsKey]);

  // Auto-calculate distance when both locations are selected from autocomplete
  useEffect(() => {
    if (pickupSelected && dropoffSelected && formData.booking_type === "transfer") {
      calculateDistance();
      // Reset the flags after triggering calculation
      setPickupSelected(false);
      setDropoffSelected(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupSelected, dropoffSelected, formData.booking_type]);

  // Handlers for when user selects from autocomplete suggestions
  const handlePickupSuggestionSelect = (value) => {
    setFormData(prev => ({ ...prev, pickup_location: value }));
    setPickupSelected(true);
    // Auto-trigger calculation when both addresses are set
    if (formData.dropoff_location) {
      setTimeout(() => calculateDistance(), 100);
    }
  };

  const handleDropoffSuggestionSelect = (value) => {
    setFormData(prev => ({ ...prev, dropoff_location: value }));
    setDropoffSelected(true);
    // Auto-trigger calculation when both addresses are set
    if (formData.pickup_location) {
      setTimeout(() => calculateDistance(), 100);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVehicleChange = (value) => {
    setFormData(prev => ({ ...prev, vehicle_type: value }));
  };

  const handleBookingTypeChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      booking_type: value,
      hours: value === "hourly" ? (pricing?.[prev.vehicle_type]?.min_hours || 2) : prev.hours
    }));
    setDistanceInfo(null);
    setPriceQuote(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pickup_location) {
      toast.error("Please enter pickup location");
      return;
    }

    if (formData.booking_type === "transfer" && !formData.dropoff_location) {
      toast.error("Please enter dropoff location for transfer booking");
      return;
    }

    // Only passenger name is required - email and phone are optional
    if (!formData.passenger_name) {
      toast.error("Please enter passenger name");
      return;
    }

    // Validate return trip details if enabled
    if (includeReturn && !returnDate) {
      toast.error("Please select a return date");
      return;
    }
    
    // Validate return time is after pickup time if same day
    if (includeReturn && returnDate) {
      const pickupDt = new Date(date);
      const [h, m] = time.split(':');
      pickupDt.setHours(parseInt(h), parseInt(m));
      
      const returnDt = new Date(returnDate);
      const [rh, rm] = returnTime.split(':');
      returnDt.setHours(parseInt(rh), parseInt(rm));
      
      if (returnDt <= pickupDt) {
        toast.error("Return time must be after pickup time");
        return;
      }
    }

    setLoading(true);

    try {
      const pickupDateTime = new Date(date);
      const [hours, minutes] = time.split(':');
      pickupDateTime.setHours(parseInt(hours), parseInt(minutes));

      // Filter out empty stops
      const filteredStops = stops.filter(s => s && s.trim() !== "");

      const bookingData = {
        ...formData,
        pickup_datetime: pickupDateTime.toISOString(),
        distance_km: distanceInfo?.distance_km || parseFloat(manualDistance) || null,
        price: parseFloat(formData.price) || priceQuote?.price || 0,
        extras: selectedExtras,
        extras_total: getExtrasTotal(),
        corporate_client_id: selectedCorporate || null,
        corporate_reference: corporateReference || null,
        reference_number: formData.reference_number || null,
        stops: filteredStops.length > 0 ? filteredStops : null,
        payment_method: (user?.role === "admin" || user?.role === "staff") ? paymentMethod : "credit_card"
      };

      // If sending as quote instead of booking
      if (sendAsQuote && (user?.role === "admin" || user?.role === "staff")) {
        // Create main quote
        const response = await api.post("/quotes", bookingData);
        let message = `Quote ${response.data.quote_ref} sent to ${formData.passenger_email}`;
        
        // If return trip is enabled, create return quote as well
        if (includeReturn && returnDate) {
          const returnDateTime = new Date(returnDate);
          const [returnHours, returnMinutes] = returnTime.split(':');
          returnDateTime.setHours(parseInt(returnHours), parseInt(returnMinutes));
          
          const returnQuoteData = {
            ...bookingData,
            pickup_location: formData.dropoff_location || bookingData.dropoff_location,
            dropoff_location: formData.pickup_location || bookingData.pickup_location,
            pickup_datetime: returnDateTime.toISOString(),
            notes: `Return trip quote`
          };
          
          const returnResponse = await api.post("/quotes", returnQuoteData);
          message = `Quotes ${response.data.quote_ref} & ${returnResponse.data.quote_ref} (return) sent to ${formData.passenger_email}`;
        }
        
        toast.success(message);
        
        const dashboardRoutes = {
          admin: "/admin",
          staff: "/staff"
        };
        navigate(dashboardRoutes[user?.role] || "/dashboard");
        return;
      }

      // Create the main booking
      const response = await api.post("/bookings", bookingData);
      const mainBookingRef = response.data.booking_ref;
      const mainBookingId = response.data.id;
      
      // If return trip is enabled, create the return booking
      let returnBookingId = null;
      if (includeReturn && returnDate) {
        const returnDateTime = new Date(returnDate);
        const [returnHours, returnMinutes] = returnTime.split(':');
        returnDateTime.setHours(parseInt(returnHours), parseInt(returnMinutes));

        const returnBookingData = {
          original_booking_id: response.data.id,
          return_datetime: returnDateTime.toISOString(),
          price: formData.price || priceQuote?.price || 0,
          notes: `Return trip for ${mainBookingRef}`
        };

        const returnResponse = await api.post("/bookings/return", returnBookingData);
        returnBookingId = returnResponse.data.id;
        toast.success(`Bookings created: ${mainBookingRef} & ${returnResponse.data.booking_ref} (return)`);
      } else {
        toast.success(`Booking created: ${mainBookingRef}`);
      }

      // Create invoice if option is selected (admin only)
      if (createInvoice && (user?.role === "admin" || user?.role === "staff")) {
        try {
          const bookingIds = returnBookingId 
            ? [mainBookingId, returnBookingId] 
            : [mainBookingId];
          
          // Determine client_id - use corporate client if selected
          // If no corporate client, we need a valid client_id
          const clientId = selectedCorporate || response.data.corporate_client_id;
          
          if (!clientId) {
            toast.error("Invoice requires a corporate client. Please select a corporate account or create the invoice separately.");
          } else {
            const invoiceResponse = await api.post("/invoices", {
              client_id: clientId,
              booking_ids: bookingIds,
              due_date: invoiceDueDate
            });
            toast.success(`Invoice ${invoiceResponse.data.invoice_number} created`);
          }
        } catch (invError) {
          console.error("Invoice creation failed:", invError);
          toast.error("Booking created but invoice creation failed: " + (invError.response?.data?.detail || "Unknown error"));
        }
      }
      
      const dashboardRoutes = {
        admin: "/admin",
        staff: "/staff",
        client: "/client"
      };
      navigate(dashboardRoutes[user?.role] || "/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    const dashboardRoutes = {
      admin: "/admin",
      staff: "/staff",
      client: "/client"
    };
    navigate(dashboardRoutes[user?.role] || "/dashboard");
  };

  // AI Assistant handler
  const handleAiBooking = async () => {
    if (!aiBookingText.trim()) return;
    
    setParsingAiBooking(true);
    try {
      const response = await api.post("/bookings/parse-nl", { text: aiBookingText });
      const parsed = response.data.parsed_booking;
      
      // Update form with parsed data
      setFormData(prev => ({
        ...prev,
        passenger_name: parsed.passenger_name || prev.passenger_name,
        passenger_email: parsed.passenger_email || prev.passenger_email,
        passenger_phone: parsed.passenger_phone || prev.passenger_phone,
        pickup_location: parsed.pickup_location || prev.pickup_location,
        dropoff_location: parsed.dropoff_location || prev.dropoff_location,
        vehicle_type: parsed.vehicle_type || prev.vehicle_type,
        num_passengers: parsed.num_passengers || prev.num_passengers,
        flight_number: parsed.flight_number || prev.flight_number,
        special_requirements: parsed.special_requirements || prev.special_requirements
      }));
      
      // Update date and time if provided
      if (parsed.pickup_date) {
        try {
          const parsedDate = new Date(parsed.pickup_date);
          if (!isNaN(parsedDate.getTime())) {
            setDate(parsedDate);
          }
        } catch (e) {
          // Date parsing failed, ignore
        }
      }
      if (parsed.pickup_time) {
        setTime(parsed.pickup_time);
      }
      
      // Clear the AI input
      setAiBookingText("");
      
      toast.success("Booking details extracted! Please review and confirm.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to parse booking. Please try again with more details.");
    } finally {
      setParsingAiBooking(false);
    }
  };

  const currentVehicle = pricing?.[formData.vehicle_type];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <button onClick={goBack} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-5 w-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <Car className="h-6 w-6 text-primary" />
              <span className="font-bold">New Booking</span>
            </div>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="pt-20 pb-8 px-4 max-w-4xl mx-auto">
        <AIBookingAssistant
          aiBookingText={aiBookingText}
          setAiBookingText={setAiBookingText}
          parsingAiBooking={parsingAiBooking}
          onParse={handleAiBooking}
        />

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6">
            
            <BookingTypeSelector
              bookingType={formData.booking_type}
              onBookingTypeChange={handleBookingTypeChange}
            />

            {/* Route Section */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  {formData.booking_type === "transfer" ? "Route Details" : "Pickup Location"}
                </CardTitle>
                <CardDescription>
                  {formData.booking_type === "transfer" 
                    ? "Enter pickup and dropoff locations" 
                    : "Enter pickup location for hourly hire"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pickup_location">Pickup Location *</Label>
                  <AddressAutocomplete
                    value={formData.pickup_location}
                    onChange={(value) => setFormData(prev => ({ ...prev, pickup_location: value }))}
                    onSuggestionSelect={handlePickupSuggestionSelect}
                    placeholder="Start typing to search (e.g., Sydney Airport)"
                    className="bg-secondary/50 border-black/10"
                    icon={MapPin}
                    iconColor="text-green-500"
                    data-testid="pickup-input"
                  />
                </div>
                
                {formData.booking_type === "transfer" && (
                  <>
                    {/* Stops Section - Between Pickup and Dropoff */}
                    <div className="space-y-3 pt-2 border-t border-white/5">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-xs text-yellow-500">+</span>
                          Via Stops (Optional)
                        </Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addStop}
                          className="text-xs"
                        >
                          + Add Stop
                        </Button>
                      </div>
                      
                      {stops.length > 0 && (
                        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleStopDragEnd}>
                          <SortableContext items={stopIds} strategy={verticalListSortingStrategy}>
                            <div className="space-y-2">
                              {stops.map((stop, index) => (
                                <SortableStopItem
                                  key={stopIds[index]}
                                  id={stopIds[index]}
                                  index={index}
                                  stop={stop}
                                  onUpdate={updateStop}
                                  onRemove={removeStop}
                                  onSelect={(idx, value) => updateStop(idx, value)}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                      
                      {stops.length === 0 && (
                        <p className="text-xs text-muted-foreground pl-8">
                          Add intermediate stops if the passenger needs to make stops along the way
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="dropoff_location">Dropoff Location *</Label>
                      <AddressAutocomplete
                        value={formData.dropoff_location}
                        onChange={(value) => setFormData(prev => ({ ...prev, dropoff_location: value }))}
                        onSuggestionSelect={handleDropoffSuggestionSelect}
                        placeholder="Start typing to search (e.g., Sydney CBD)"
                        className="bg-secondary/50 border-black/10"
                        icon={MapPin}
                        iconColor="text-red-500"
                        data-testid="dropoff-input"
                      />
                    </div>
                    
                    {/* Distance Calculator */}
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center gap-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={calculateDistance}
                          disabled={calculatingDistance || !formData.pickup_location || !formData.dropoff_location}
                          className="flex items-center gap-2"
                          data-testid="calculate-distance-btn"
                        >
                          {calculatingDistance ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Navigation className="h-4 w-4" />
                          )}
                          Auto-Calculate
                        </Button>
                        
                        {distanceInfo && (
                          <div className="flex items-center gap-4 text-sm">
                            <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/30">
                              {distanceInfo.distance_text}
                            </Badge>
                            <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30">
                              ~{distanceInfo.duration_text}
                            </Badge>
                          </div>
                        )}
                      </div>
                      
                      {/* Manual Distance Input */}
                      <div className="flex items-center gap-4">
                        <div className="flex-1 max-w-[200px]">
                          <Label htmlFor="manual_distance" className="text-xs text-muted-foreground">Or enter distance manually:</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              id="manual_distance"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="e.g., 25"
                              value={manualDistance}
                              onChange={(e) => handleManualDistanceChange(e.target.value)}
                              className="bg-secondary/50 border-black/10 w-24"
                              data-testid="manual-distance-input"
                            />
                            <span className="text-sm text-muted-foreground">km</span>
                          </div>
                        </div>
                        {priceQuote && parseFloat(manualDistance) > 0 && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Estimated: </span>
                            <span className="font-bold text-primary">${(priceQuote.price || 0).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      
                      {distanceError && (
                        <p className="text-xs text-yellow-500">
                          {distanceError.includes("billing") ? "Google Maps billing required. Please enter distance manually or use fixed route prices." : distanceError}
                        </p>
                      )}
                      
                      {/* Route Map Preview */}
                      <RouteMapPreview
                        pickup={formData.pickup_location}
                        dropoff={formData.dropoff_location}
                        stops={stops}
                      />
                    </div>
                  </>
                )}
                
                {formData.booking_type === "hourly" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="hours">Number of Hours *</Label>
                      <Select 
                        value={formData.hours.toString()} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, hours: parseInt(v) }))}
                      >
                        <SelectTrigger className="bg-secondary/50 border-black/10" data-testid="hours-select">
                          <Clock className="h-4 w-4 mr-2" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[2, 3, 4, 5, 6, 7, 8, 10, 12].map(h => (
                            <SelectItem 
                              key={h} 
                              value={h.toString()}
                              disabled={h < (currentVehicle?.min_hours || 2)}
                            >
                              {h} hours {h < (currentVehicle?.min_hours || 2) ? `(min ${currentVehicle?.min_hours}h)` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Minimum booking: {currentVehicle?.min_hours || 2} hours for {currentVehicle?.name || 'this vehicle'}
                      </p>
                    </div>
                    
                    {/* Optional Dropoff for Hourly */}
                    <div className="space-y-2">
                      <Label>Drop-off Location (Optional)</Label>
                      <AddressAutocomplete
                        value={formData.dropoff_location}
                        onChange={(val) => setFormData(prev => ({ ...prev, dropoff_location: val }))}
                        onSuggestionSelect={(place) => {
                          setFormData(prev => ({ ...prev, dropoff_location: place.description || place }));
                        }}
                        placeholder="Leave empty if returning to pickup location"
                        className="bg-secondary/50 border-black/10"
                        icon={MapPin}
                        iconColor="text-red-500"
                        data-testid="hourly-dropoff-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Where should the driver drop you off at the end? Leave empty for round trip.
                      </p>
                    </div>
                    
                    {/* Via Stops for Hourly */}
                    <div className="space-y-3 pt-2 border-t border-white/5">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-xs text-yellow-500">+</span>
                          Via Stops (Optional)
                        </Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addStop}
                          className="text-xs"
                        >
                          + Add Stop
                        </Button>
                      </div>
                      
                      {stops.length > 0 && (
                        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleStopDragEnd}>
                          <SortableContext items={stopIds} strategy={verticalListSortingStrategy}>
                            <div className="space-y-2">
                              {stops.map((stop, index) => (
                                <SortableStopItem
                                  key={stopIds[index]}
                                  id={stopIds[index]}
                                  index={index}
                                  stop={stop}
                                  onUpdate={updateStop}
                                  onRemove={removeStop}
                                  onSelect={(idx, value) => updateStop(idx, value)}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                      
                      {stops.length === 0 && (
                        <p className="text-xs text-muted-foreground pl-8">
                          Add locations you would like to visit during the hire
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Date & Time Section */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                  Date & Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pickup Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left bg-secondary/50 border-black/10" data-testid="date-picker-trigger">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => d && setDate(d)}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="time">Pickup Time *</Label>
                    <div className="relative">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="time"
                        type="time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        className="pl-10 bg-secondary/50 border-black/10"
                        required
                        data-testid="time-input"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Corporate Account Selection (Admin/Staff only) */}
            {(user?.role === "admin" || user?.role === "staff") && corporateClients.length > 0 && (
              <Card className="dashboard-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="h-5 w-5 text-primary" />
                    Corporate Account
                  </CardTitle>
                  <CardDescription>Select a corporate client to book on their behalf</CardDescription>
                </CardHeader>
                <CardContent>
                  <Select value={selectedCorporate || "none"} onValueChange={(v) => handleCorporateSelect(v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-secondary/50 border-black/10" data-testid="corporate-select">
                      <SelectValue placeholder="Select corporate account (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No corporate account</SelectItem>
                      {corporateClients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name} ({client.name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedCorporate && (
                    <div className="space-y-3 mt-3">
                      <p className="text-sm text-muted-foreground">
                        Enter passenger details below for this corporate booking
                      </p>
                      <div className="space-y-2">
                        <Label htmlFor="corporate_reference">Your Reference Number (optional)</Label>
                        <Input
                          id="corporate_reference"
                          placeholder="e.g., PO-12345, Cost Centre: ABC"
                          value={corporateReference}
                          onChange={(e) => setCorporateReference(e.target.value)}
                          className="bg-secondary/50 border-black/10"
                          data-testid="corporate-reference-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          This reference will appear on your invoice for easy tracking
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Passenger Details */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Passenger Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="passenger_name">Full Name *</Label>
                    <Input
                      id="passenger_name"
                      name="passenger_name"
                      placeholder="John Smith"
                      value={formData.passenger_name}
                      onChange={handleChange}
                      className="bg-secondary/50 border-black/10"
                      required
                      data-testid="passenger-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="num_passengers">Number of Passengers</Label>
                    <Select value={formData.num_passengers.toString()} onValueChange={(v) => setFormData(prev => ({ ...prev, num_passengers: parseInt(v) }))}>
                      <SelectTrigger className="bg-secondary/50 border-black/10" data-testid="passengers-select">
                        <Users className="h-4 w-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n} passenger{n > 1 ? 's' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="passenger_email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="passenger_email"
                        name="passenger_email"
                        type="email"
                        placeholder="john@example.com (optional)"
                        value={formData.passenger_email}
                        onChange={handleChange}
                        className="pl-10 bg-secondary/50 border-black/10"
                        data-testid="passenger-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="passenger_phone">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="passenger_phone"
                        name="passenger_phone"
                        type="tel"
                        placeholder="+61 400 000 000 (optional)"
                        value={formData.passenger_phone}
                        onChange={handleChange}
                        className="pl-10 bg-secondary/50 border-black/10"
                        data-testid="passenger-phone-input"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <VehicleSelection
              pricing={pricing}
              vehicleType={formData.vehicle_type}
              bookingType={formData.booking_type}
              hours={formData.hours}
              currentVehicle={currentVehicle}
              flightNumber={formData.flight_number}
              flightArrivalTime={formData.flight_arrival_time}
              onVehicleChange={handleVehicleChange}
              onHoursChange={(v) => setFormData(prev => ({ ...prev, hours: parseInt(v) }))}
              onFieldChange={handleChange}
            />

            {/* Additional Info */}
            <Card className="dashboard-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-primary" />
                  Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="special_requirements">Special Requirements</Label>
                  <Textarea
                    id="special_requirements"
                    name="special_requirements"
                    placeholder="Child seat, wheelchair access, extra luggage..."
                    value={formData.special_requirements}
                    onChange={handleChange}
                    className="bg-secondary/50 border-black/10 min-h-[80px]"
                    data-testid="special-requirements-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Internal Notes</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    placeholder="Any additional notes for staff..."
                    value={formData.notes}
                    onChange={handleChange}
                    className="bg-secondary/50 border-black/10 min-h-[80px]"
                    data-testid="notes-input"
                  />
                </div>
                
                {/* Reference Number - shown for admin/staff */}
                {(user?.role === "admin" || user?.role === "staff") && (
                  <div className="space-y-2">
                    <Label htmlFor="reference_number">Reference / File Number</Label>
                    <Input
                      id="reference_number"
                      name="reference_number"
                      placeholder="Enter reservation or file number..."
                      value={formData.reference_number}
                      onChange={handleChange}
                      className="bg-secondary/50 border-black/10"
                      data-testid="reference-number-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      This reference will appear on invoices sent to corporate clients
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {formData.booking_type === "transfer" && (
              <ReturnTripSection
                includeReturn={includeReturn}
                setIncludeReturn={setIncludeReturn}
                returnDate={returnDate}
                setReturnDate={setReturnDate}
                returnTime={returnTime}
                setReturnTime={setReturnTime}
                pickupDate={date}
                pickupLocation={formData.pickup_location}
                dropoffLocation={formData.dropoff_location}
              />
            )}

            <ExtrasSelection
              extras={extras}
              selectedExtras={selectedExtras}
              onToggle={handleExtraToggle}
              extrasTotal={getExtrasTotal()}
            />

            {/* Pricing — Auto-Calculate + Override (for staff/admin) */}
            {(user?.role === "admin" || user?.role === "staff") && (
              <Card className="dashboard-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Auto-calculated distance & price info */}
                  {distanceInfo && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm text-blue-400">
                          <Navigation className="h-4 w-4" />
                          Auto-Calculated
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => calculateDistance()}
                          disabled={calculatingDistance}
                          className="text-xs"
                          data-testid="recalculate-btn"
                        >
                          <RefreshCw className={`h-3 w-3 mr-1 ${calculatingDistance ? 'animate-spin' : ''}`} />
                          Recalculate
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-2">
                        <div>
                          <p className="text-xs text-muted-foreground">Distance</p>
                          <p className="font-bold text-sm">{distanceInfo.distance_text}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Duration</p>
                          <p className="font-bold text-sm">~{distanceInfo.duration_text}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Suggested Price</p>
                          <p className="font-bold text-sm text-primary">${parseFloat(formData.price || 0).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {calculatingDistance && (
                    <div className="flex items-center justify-center py-3 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Calculating route & price...
                    </div>
                  )}
                  
                  {distanceError && (
                    <p className="text-xs text-amber-400">{distanceError} — Enter distance and price manually below.</p>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="manual_distance">Distance (km)</Label>
                      <Input
                        id="manual_distance"
                        type="number"
                        step="0.1"
                        min="0"
                        value={manualDistance}
                        onChange={(e) => setManualDistance(e.target.value)}
                        placeholder="e.g., 85.6"
                        className="bg-secondary/50 border-black/10"
                        data-testid="manual-distance-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price">Price (AUD) — Override</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="price"
                          name="price"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.price}
                          onChange={handleChange}
                          className="pl-10 bg-secondary/50 border-black/10"
                          data-testid="price-input"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Price auto-fills from route calculation. You can override it at any time.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Summary & Submit */}
            <Card className="dashboard-card bg-primary/5 border-primary/20">
              <CardContent className="p-6">
                <div className="flex flex-col gap-4">
                  {/* Admin: Create Invoice Option */}
                  {(user?.role === "admin" || user?.role === "staff") && (
                    <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg border border-black/10">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={createInvoice}
                          onCheckedChange={setCreateInvoice}
                          id="create-invoice"
                          data-testid="create-invoice-switch"
                          disabled={!selectedCorporate}
                        />
                        <Label htmlFor="create-invoice" className="cursor-pointer">
                          <span className="font-medium">Create Invoice</span>
                          <p className="text-xs text-muted-foreground">
                            {selectedCorporate 
                              ? "Generate invoice immediately after booking" 
                              : "Select a corporate account above to enable invoice creation"}
                          </p>
                        </Label>
                      </div>
                      {createInvoice && selectedCorporate && (
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground whitespace-nowrap">Due:</Label>
                          <Input
                            type="date"
                            value={invoiceDueDate}
                            onChange={(e) => setInvoiceDueDate(e.target.value)}
                            className="w-[140px] h-8 text-xs bg-secondary/50 border-black/10"
                            data-testid="invoice-due-date"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Admin/Staff: Payment Method Selection */}
                  {(user?.role === "admin" || user?.role === "staff") && (
                    <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                      <Label className="text-sm font-medium mb-2 block">
                        <DollarSign className="h-4 w-4 inline mr-2 text-green-400" />
                        Payment Method
                      </Label>
                      <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                        <SelectTrigger className="w-full bg-secondary/50 border-black/10" data-testid="payment-method-select">
                          <SelectValue placeholder="Select payment method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="credit_card">Credit Card</SelectItem>
                          <SelectItem value="invoice">Invoice</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        {paymentMethod === "credit_card" && "Customer will pay via credit card"}
                        {paymentMethod === "invoice" && "Customer will be invoiced after service"}
                        {paymentMethod === "cash" && "Customer will pay cash to driver"}
                        {paymentMethod === "bank_transfer" && "Customer will pay via bank transfer"}
                      </p>
                    </div>
                  )}

                  {/* Admin/Staff: Send as Email Quote Option */}
                  {(user?.role === "admin" || user?.role === "staff") && (
                    <div className="flex items-center justify-between p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={sendAsQuote}
                          onCheckedChange={(checked) => {
                            setSendAsQuote(checked);
                            if (checked) setCreateInvoice(false); // Disable invoice if sending quote
                          }}
                          id="send-as-quote"
                          data-testid="send-as-quote-switch"
                        />
                        <Label htmlFor="send-as-quote" className="cursor-pointer">
                          <span className="font-medium flex items-center gap-2">
                            <Mail className="h-4 w-4 text-blue-400" />
                            Send as Email Quote
                          </span>
                          <p className="text-xs text-muted-foreground">
                            Email quote to customer for approval. Booking created after payment.
                          </p>
                        </Label>
                      </div>
                      {sendAsQuote && (
                        <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          Valid 7 days
                        </Badge>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {priceQuote?.price_type === "hourly" 
                          ? `Hourly Booking (${formData.hours} hours)`
                          : priceQuote?.price_type === "fixed_route"
                          ? "Fixed Route Price"
                          : "Estimated Price"}
                        {includeReturn && " (includes return)"}
                      </p>
                      <p className="text-3xl font-bold text-primary" data-testid="total-price">
                        ${((getTotalPrice() || 0) * (includeReturn ? 2 : 1)).toFixed(2)} AUD
                      </p>
                      {selectedExtras.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Base: ${(formData.price || 0).toFixed(2)} + Extras: ${(getExtrasTotal() || 0).toFixed(2)}
                        </p>
                      )}
                      {includeReturn && (
                        <p className="text-xs text-muted-foreground mt-1">
                          ${(formData.price || priceQuote?.price || 0).toFixed(2)} × 2 trips
                        </p>
                      )}
                      {priceQuote?.breakdown && !includeReturn && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {priceQuote.breakdown.type}
                          {priceQuote.breakdown.distance_km && ` • ${priceQuote.breakdown.distance_km} km`}
                        </p>
                      )}
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className={`rounded-full px-8 py-6 text-lg font-semibold ${
                        sendAsQuote 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : "bg-primary text-primary-foreground hover:bg-primary/90 glow-primary"
                      }`}
                      data-testid="submit-booking-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          {sendAsQuote ? "Sending Quote..." : "Creating..."}
                        </>
                      ) : sendAsQuote ? (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Send Quote to Customer
                        </>
                      ) : includeReturn ? (
                        createInvoice ? "Create Bookings & Invoice" : "Create Both Bookings"
                      ) : (
                        createInvoice ? "Create Booking & Invoice" : "Create Booking"
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </form>
      </main>
    </div>
  );
};

export default BookingForm;
